import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Announcement, CreateAnnouncementPayload, UpdateAnnouncementPayload } from '../types';

/** Canonical query key — shared by the page, drawer, and dashboard widgets. */
export const ANNOUNCEMENTS_KEY = ['announcements', 'list'] as const;

interface AnnouncementsResponse {
  announcements: Announcement[];
}

/**
 * Single authoritative announcements data hook. The Announcements page, detail
 * drawer, and every dashboard widget consume this exact hook/key, so there is
 * one cache and one GET /api/announcements request regardless of surfaces open.
 */
export function useAnnouncements() {
  return useQuery({
    queryKey: ANNOUNCEMENTS_KEY,
    queryFn: async () => {
      const { data } = await api.get<AnnouncementsResponse>('/api/announcements');
      return data.announcements ?? [];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useAnnouncementMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['announcements'] });

  const createAnnouncement = useMutation({
    mutationFn: async (payload: CreateAnnouncementPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/announcements', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateAnnouncement = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateAnnouncementPayload }) => {
      const { data } = await api.put<{ ok: boolean }>(`/api/announcements/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteAnnouncement = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/announcements/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createAnnouncement, updateAnnouncement, deleteAnnouncement };
}
